import partition from 'lodash/partition';
import {Component} from 'panel';

import {THEME_ENUM, THEMES} from '../../components/button';
import {MENU_ALIGNMENT} from '../../components/select';
import {registerMPElement} from '../../util/register-element';

import template from './index.jade';
import css from './index.styl?inline';
interface ControlBarState {
  menuOpen: boolean;
}
export interface ControlItem {
  disabled?: boolean;
  icon?: string;
  theme?: THEME_ENUM;
  // We only support a subset of tooltip options
  // as tooltips are placed and controlled based
  // on contextual position of each control item
  tooltip?: {
    text: string;
    type?: string;
    placement?: string;
  };
  slot?: string;
  value: string | number;
  label: string;
}
export default registerMPElement(
  `mp-control-bar`,
  class extends Component<ControlBarState> {
    static get attrsSchema() {
      return {
        ...super.attrsSchema,
        controls: {
          type: `json`,
          default: [],
          description: `interface: ControlItem[]`,
        },
        theme: {
          type: `string`,
          description: `Theme applied to untruncated buttons`,
          default: `tertiary`,
          enum: THEMES,
        },
        size: {
          type: `string`,
          description: `Button size applied to untruncated buttons`,
          default: `small`,
        },
        'overflow-controls': {
          type: `json`,
          default: [],
          description: `interface: ControlItem[]`,
        },
        'overflow-icon': {
          type: `string`,
          description: `Custom overflow icon for the mp-select dropdown button`,
          default: `hor-ellipsis`,
        },
        'overflow-menu-width': {
          type: `number`,
          default: 160,
        },
        'overflow-menu-alignment': {
          type: `string`,
          default: `right`,
          enum: Object.values(MENU_ALIGNMENT),
        },
      };
    }

    get config() {
      return {
        css,
        template,
        useShadowDom: true,
        defaultState: {
          menuOpen: false,
        },
        helpers: {
          openMenu: () => this.update({menuOpen: true}),
          onDropMenuStateChange: (ev) => this.update({menuOpen: ev.detail.state === `open`}),

          getControls: (): [[ControlItem] | any, [ControlItem] | any] => {
            const controls = this.attr(`controls`);

            return partition(controls, (item) => !item.truncate);
          },

          getOverflowItems: () => {
            const items = this.attr(`overflow-controls`);
            const isDirectSection = items.length && items[0].hasOwnProperty(`items`);
            const totalItems = isDirectSection
              ? items.reduce((a, section) => a + section.items.length, 0)
              : items.length;

            if (totalItems < 1) {
              return [];
            }

            // if passing sections directly pass that
            if (isDirectSection) {
              return items.map((section) => ({...section, items: this.mapItems(section.items)}));
            }

            return items.length ? [{items: this.mapItems(items)}] : [];
          },

          getContextualOverflowTooltip: () => {
            const labels = this.helpers
              .getOverflowItems()
              .reduce((a, b) => [...a, ...b.items], [])
              .map((item) => item.shortLabel || item.label)
              .filter(Boolean);

            if (!labels.length) {
              return null;
            }

            return {
              text: `${labels.slice(0, 2).join(`, `)}${labels.length > 2 ? `, and more...` : ``}`,
            };
          },

          handleMenuClick: (ev) => {
            const {value} = ev.detail.item;

            this.dispatchEvent(new CustomEvent(`select`, {detail: {value}}));
            this.update({menuOpen: false});
          },

          handleClick: (ev) => {
            const value = ev.target.getAttribute(`value`);
            this.selectOption(value);
          },
        },
      };
    }

    public selectOption(value) {
      this.dispatchEvent(new CustomEvent(`select`, {detail: {value}}));
    }

    private mapItems(items: ControlItem[]) {
      return items.map((item) => ({...item, isDisabled: item.disabled}));
    }
  },
);
